import React, { useCallback, useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { IoClose } from "react-icons/io5";
import { DEFAULT_MAX_OTP_SENDS, OTP_ACTION_RESEND, OTP_ACTION_SEND, OTP_PURPOSE_EMAIL_VERIFICATION, OTP_ROLE_CAREGIVER, OTP_ROLE_FAMILY_MEMBER, sendEmailOtp, sendMobileOtp, validateEmailOtp, validateMobileOtp } from "../../common/api/familyportalOtpApi";
import { PROFILE_ROLE_CAREGIVER, PROFILE_ROLE_FAMILY_MEMBER, sendEditMobileOtp, validateEditMobileOtp } from "../../common/api/profileApi";
import {
  clearPersistedEmailVerifyResendLock,
  clearPersistedResendCooldown,
  formatResendLockError,
  getCooldownRemainingSeconds,
  isOtpWaitMessage,
  isSendOtpRateLimitError,
  isSendOtpTooSoonError,
  parseLockedUntilMs,
  readPersistedEmailVerifyResendLock,
  readPersistedResendCooldown,
  resolveEmailVerifyLockEndsAt,
  resolveResendCooldownEndsAt,
  writePersistedEmailVerifyResendLock,
  writePersistedResendCooldown,
} from "../../common/utils/forgotPasswordResendLock";
import { CONTACT_VERIFICATION_ROLE } from "../../common/utils/contactVerification";
import { isFlutterInAppWebView } from "../../common/utils/webView";
import * as Sentry from "@sentry/react";

export const EMAIL_VERIFY_SUCCESS_MESSAGE = "Email verified successfully.";
export const OTP_MODE_VERIFY = "verify";
export const OTP_MODE_EDIT_MOBILE = "editMobile";

const EMAIL_OTP_LENGTH = 6;
const MOBILE_OTP_LENGTH = 6;
const RESEND_LOCK_MS = 15 * 60 * 1000;
const OTP_ERROR_CLEAR_MS = 5000;

const buildEmptyDigits = (len) => Array.from({ length: len }, () => "");

const resolveOtpApiRole = (verificationRole) => (verificationRole === CONTACT_VERIFICATION_ROLE.CAREGIVER ? OTP_ROLE_CAREGIVER : OTP_ROLE_FAMILY_MEMBER);

const resolveProfileApiRole = (verificationRole) => (verificationRole === CONTACT_VERIFICATION_ROLE.CAREGIVER ? PROFILE_ROLE_CAREGIVER : PROFILE_ROLE_FAMILY_MEMBER);

const FamilyOtpVerifyDialog = ({ open, onClose, target, email, mobileNumber, contactDisplay, verificationRole = CONTACT_VERIFICATION_ROLE.FAMILY, onSuccess, onSendRateLimited, initialOtpMeta = null, mode = OTP_MODE_VERIFY }) => {
  const isEmailTarget = target === "email" && Boolean(email);
  const isMobileTarget = !isEmailTarget && Boolean(mobileNumber);
  const isEditMobileMode = mode === OTP_MODE_EDIT_MOBILE && !isEmailTarget;
  const otpLength = isEmailTarget ? EMAIL_OTP_LENGTH : MOBILE_OTP_LENGTH;
  const otpApiRole = resolveOtpApiRole(verificationRole);
  const profileApiRole = resolveProfileApiRole(verificationRole);

  const [digits, setDigits] = useState(() => buildEmptyDigits(otpLength));
  const [activeIndex, setActiveIndex] = useState(0);
  const [error, setError] = useState("");
  const [sendError, setSendError] = useState("");
  const [resent, setResent] = useState(false);
  const [otpMeta, setOtpMeta] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendCooldownEndsAt, setResendCooldownEndsAt] = useState(null);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [resendLockEndsAt, setResendLockEndsAt] = useState(null);
  const [resendLockSeconds, setResendLockSeconds] = useState(0);

  const inputRefs = useRef([]);
  const errorTimerRef = useRef(null);
  const otpAutofillRequestIdRef = useRef(0);
  const sendInitialOtpRef = useRef(null);
  const lastSubmittedOtpRef = useRef("");
  const initialSendRequestedRef = useRef(false);
  const openRef = useRef(open);
  openRef.current = open;
  const initialOtpMetaRef = useRef(initialOtpMeta);
  initialOtpMetaRef.current = initialOtpMeta;

  const otp = digits.join("");

  const isResendCooldownActive = resendCooldownSeconds > 0;
  const isResendLockActive = resendLockSeconds > 0 || Boolean(resendLockEndsAt && resendLockEndsAt > Date.now());
  const displayedLockSeconds = resendLockSeconds > 0 ? resendLockSeconds : resendLockEndsAt ? Math.max(0, Math.ceil((resendLockEndsAt - Date.now()) / 1000)) : 0;

  // The boxes must be mounted on the very first commit: the first one has to
  // exist to take focus from the keyboard primer, otherwise focus lands nowhere
  // and iOS drops the keyboard. otpSent only turns true in an effect, which is
  // one commit too late. They are still withheld when a send failed outright and
  // there is nothing to type.
  const isSendingInitialCode = isSending && !otpSent;
  const hasSendBlockingError = isResendLockActive || Boolean(sendError);
  const otpReady = otpSent || (!hasSendBlockingError && (isMobileTarget || isEmailTarget));
  // Auto-submit only once a code is genuinely out, so a paste into the boxes
  // before that cannot be fired at the API early.
  const canSubmitOtp = otpSent || isMobileTarget;

  const focusFirstOtpBox = useCallback(() => {
    const focus = () => {
      const input = inputRefs.current[0];
      if (!input) {
        return;
      }
      input.focus();
      input.select?.();
    };

    focus();
    window.setTimeout(focus, 80);
  }, []);

  const clearErrorTimer = useCallback(() => {
    if (errorTimerRef.current) {
      window.clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
  }, []);

  // Android autofill: ask the app bridge for the incoming SMS code. The promise
  // can stay pending until the user taps "Allow" on the system dialog, so this
  // never blocks the UI. requestId guards against a stale resolution landing
  // after the dialog closed/reopened or a resend re-armed the request.
  const requestSmsAutofill = useCallback(() => {
    if (!isFlutterInAppWebView()) {
      return;
    }
    const requestId = ++otpAutofillRequestIdRef.current;
    window.flutter_inappwebview
      .callHandler("requestOtpAutofill")
      .then((code) => {
        if (!code || otpAutofillRequestIdRef.current !== requestId) {
          return;
        }
        setDigits(buildEmptyDigits(otpLength).map((_, i) => code[i] || ""));
        clearErrorTimer();
        setError("");
      })
      .catch((error) => {
        Sentry.captureException(error);
      });
  }, [otpLength, clearErrorTimer]);

  useEffect(() => () => clearErrorTimer(), [clearErrorTimer]);
  useEffect(() => {
    if (!resendCooldownEndsAt) {
      setResendCooldownSeconds(0);
      return;
    }
    const tick = () => {
      const rem = Math.max(0, Math.ceil((resendCooldownEndsAt - Date.now()) / 1000));
      setResendCooldownSeconds(rem);
      if (rem === 0) {
        setResendCooldownEndsAt(null);
        clearPersistedResendCooldown();
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [resendCooldownEndsAt]);

  const startResendLock = useCallback((emailAddr, data) => {
    setResendCooldownEndsAt(null);
    setResendCooldownSeconds(0);
    clearPersistedResendCooldown();

    const lockEndsAt = resolveEmailVerifyLockEndsAt({
      email: emailAddr,
      lockedUntil: data?.lockedUntil,
      attemptsRemaining: data?.attemptsRemaining ?? 0,
    });
    if (!lockEndsAt) {
      return;
    }

    setResendLockEndsAt(lockEndsAt);
    writePersistedEmailVerifyResendLock(emailAddr, lockEndsAt);
  }, []);

  useEffect(() => {
    if (!resendLockEndsAt) {
      setResendLockSeconds(0);
      return;
    }
    const tick = () => {
      const rem = Math.max(0, Math.ceil((resendLockEndsAt - Date.now()) / 1000));
      setResendLockSeconds(rem);
      if (rem === 0) {
        clearPersistedEmailVerifyResendLock();
        setResendLockEndsAt(null);
        setOtpMeta(null);
        setSendError("");
        if (openRef.current) {
          sendInitialOtpRef.current?.();
        }
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [resendLockEndsAt]);

  const startResendCooldown = useCallback((data, emailAddr, message = "") => {
    const cooldownEndsAt = resolveResendCooldownEndsAt(data?.nextRequestAfter, message, data?.minRequestIntervalSeconds);
    if (!cooldownEndsAt) {
      return;
    }

    setResendCooldownEndsAt(cooldownEndsAt);
    setResendCooldownSeconds(getCooldownRemainingSeconds(cooldownEndsAt));
    writePersistedResendCooldown(emailAddr, cooldownEndsAt);
  }, []);

  const applyOtpMeta = useCallback(
    (data, emailAddr, message = "") => {
      if (!data) return;
      setOtpMeta(data);
      startResendCooldown(data, emailAddr, message);

      if (data.attemptsRemaining === 0 || data.lockedUntil) {
        const lockEndsAt = resolveEmailVerifyLockEndsAt({
          email: emailAddr,
          lockedUntil: data.lockedUntil,
          attemptsRemaining: data.attemptsRemaining,
        });
        if (lockEndsAt) {
          setResendLockEndsAt(lockEndsAt);
          writePersistedEmailVerifyResendLock(emailAddr, lockEndsAt);
        }
        return;
      }

      clearPersistedEmailVerifyResendLock();
      setResendLockEndsAt(null);
    },
    [startResendCooldown],
  );

  const showOtpError = useCallback(
    (message) => {
      clearErrorTimer();
      setError(message);
      errorTimerRef.current = window.setTimeout(() => {
        setError("");
        errorTimerRef.current = null;
      }, OTP_ERROR_CLEAR_MS);
    },
    [clearErrorTimer],
  );

  const sendInitialOtp = useCallback(async () => {
    if (!isEmailTarget || !email) {
      return;
    }

    const activeLock = readPersistedEmailVerifyResendLock(email);
    if (activeLock && activeLock > Date.now()) {
      setResendLockEndsAt(activeLock);
      setSendError("");
      return;
    }

    setIsSending(true);
    setSendError("");
    try {
      const result = await sendEmailOtp({
        email,
        purpose: OTP_PURPOSE_EMAIL_VERIFICATION,
        action: OTP_ACTION_SEND,
        role: otpApiRole,
      });

      if (!result.success) {
        if (result.data) {
          setOtpMeta(result.data);
        }

        if (isSendOtpRateLimitError(result.data)) {
          const lockEndsAt = resolveEmailVerifyLockEndsAt({
            email,
            lockedUntil: result.data?.lockedUntil,
            attemptsRemaining: result.data?.attemptsRemaining ?? 0,
          });
          startResendLock(email, result.data);
          if (lockEndsAt) {
            onSendRateLimited?.(lockEndsAt);
          }
          onClose();
          return;
        }

        if (isSendOtpTooSoonError(result.data, result.message) || isOtpWaitMessage(result.message)) {
          if (result.data) {
            applyOtpMeta(result.data, email, result.message);
          } else {
            startResendCooldown(null, email, result.message);
          }
          setSendError("");
          setOtpSent(true);
          return;
        }

        setSendError(result.message || "Failed to send verification code.");
        return;
      }

      applyOtpMeta(result.data, email);
      setOtpSent(true);
    } catch (error) {
      Sentry.captureException(error);
      setSendError("Failed to send verification code.");
    } finally {
      setIsSending(false);
    }
  }, [applyOtpMeta, email, isEmailTarget, onClose, onSendRateLimited, otpApiRole, startResendCooldown, startResendLock]);

  sendInitialOtpRef.current = sendInitialOtp;

  useEffect(() => {
    if (!open) {
      initialSendRequestedRef.current = false;
      return;
    }

    const emptyDigits = buildEmptyDigits(isEmailTarget ? EMAIL_OTP_LENGTH : MOBILE_OTP_LENGTH);
    setDigits(emptyDigits);
    setActiveIndex(0);
    setError("");
    setSendError("");
    setResent(false);
    setOtpMeta(null);
    setOtpSent(false);
    setResendCooldownEndsAt(null);
    setResendLockEndsAt(null);
    lastSubmittedOtpRef.current = "";
    clearErrorTimer();

    // The caller sends the code before opening this dialog, so the meta it
    // hands over is already available on the first commit.
    const openedWithMeta = initialOtpMetaRef.current;
    if (openedWithMeta) {
      setOtpMeta(openedWithMeta);
      const cooldownEndsAt = resolveResendCooldownEndsAt(openedWithMeta.nextRequestAfter, "", openedWithMeta.minRequestIntervalSeconds);
      if (cooldownEndsAt) {
        setResendCooldownEndsAt(cooldownEndsAt);
        setResendCooldownSeconds(getCooldownRemainingSeconds(cooldownEndsAt));
      }
    }

    if (!isEmailTarget || openedWithMeta) {
      setOtpSent(true);
      return;
    }

    const persisted = readPersistedEmailVerifyResendLock(email);
    if (persisted && persisted > Date.now()) {
      setResendLockEndsAt(persisted);
      return;
    }

    const persistedCooldown = readPersistedResendCooldown(email);
    if (persistedCooldown) {
      setResendCooldownEndsAt(persistedCooldown);
      setResendCooldownSeconds(getCooldownRemainingSeconds(persistedCooldown));
    }

    // Only reached when the dialog was opened without a code already sent.
    // Guarded so it fires once per open.
    if (initialSendRequestedRef.current) {
      return;
    }
    initialSendRequestedRef.current = true;
    sendInitialOtpRef.current?.();
    // initialOtpMeta and sendInitialOtp are read through refs so that a parent
    // re-render cannot re-run this effect and wipe digits already typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, email, isEmailTarget, clearErrorTimer]);

  useEffect(() => {
    if (!open || !otpReady) {
      return;
    }
    focusFirstOtpBox();
  }, [open, otpReady, focusFirstOtpBox]);

  useEffect(() => {
    if (!open || !isMobileTarget || !otpReady) {
      return undefined;
    }
    requestSmsAutofill();
    return () => {
      if (isFlutterInAppWebView()) {
        window.flutter_inappwebview.callHandler("cancelOtpAutofill");
      }
    };
  }, [open, isMobileTarget, otpReady, requestSmsAutofill]);

  // iOS autofill (and some Android fallback paths) write the whole code into
  // whichever box is focused as a single string, instead of one digit at a
  // time. Route that case through the same fill-all-boxes logic as paste
  // instead of collapsing it to a single character.
  const fillAllOtpBoxes = (rawValue) => {
    const code = rawValue.replace(/\D/g, "").slice(0, otpLength);
    if (!code) return;
    const next = buildEmptyDigits(otpLength).map((_, i) => code[i] || "");
    setDigits(next);
    clearErrorTimer();
    setError("");
    const focusIndex = Math.min(code.length, otpLength - 1);
    inputRefs.current[focusIndex]?.focus();
    setActiveIndex(focusIndex);
  };

  const updateDigit = (index, value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length > 1) {
      fillAllOtpBoxes(cleaned);
      return;
    }
    const digit = cleaned.slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    clearErrorTimer();
    setError("");
    if (digit && index < otpLength - 1) {
      inputRefs.current[index + 1]?.focus();
      setActiveIndex(index + 1);
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      if (digits[index]) {
        const next = [...digits];
        next[index] = "";
        setDigits(next);
        clearErrorTimer();
        setError("");
        return;
      }
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        setActiveIndex(index - 1);
      }
    }
    if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setActiveIndex(index - 1);
    }
    if (event.key === "ArrowRight" && index < otpLength - 1) {
      inputRefs.current[index + 1]?.focus();
      setActiveIndex(index + 1);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    fillAllOtpBoxes(event.clipboardData.getData("text"));
  };

  // iOS delivers the tapped code-suggestion (and some autofill paths) as a
  // beforeinput carrying the full 6-digit string in event.data. maxLength
  // truncates it to 1 char before onChange ever sees it, so this has to be
  // intercepted here, ahead of that truncation.
  const handleBeforeInput = (event) => {
    const data = event.data ?? event.nativeEvent?.data ?? "";
    const code = data.replace(/\D/g, "");
    if (code.length > 1) {
      event.preventDefault();
      fillAllOtpBoxes(code);
    }
  };

  const sendsRemaining = otpMeta?.attemptsRemaining ?? DEFAULT_MAX_OTP_SENDS - 1;
  const canResend = (isEmailTarget || Boolean(mobileNumber)) && sendsRemaining > 0 && !isSending && !isVerifying && !isResendCooldownActive && !isResendLockActive;

  const getResendButtonLabel = () => {
    if (isSending) {
      return "Sending…";
    }
    if (isResendCooldownActive) {
      return `Resend (${resendCooldownSeconds}s)`;
    }
    return `Resend${otpMeta ? ` (${sendsRemaining} left)` : ""}`;
  };

  const verifyOtp = useCallback(
    async (code) => {
      if (code.length !== otpLength || isVerifying) {
        return;
      }

      clearErrorTimer();
      setError("");
      setIsVerifying(true);

      try {
        if (!isEmailTarget) {
          const result = isEditMobileMode
            ? await validateEditMobileOtp({
                emailId: email,
                role: profileApiRole,
                mobileNumber,
                otpCode: code,
              })
            : await validateMobileOtp({
                mobileNumber,
                otpCode: code,
                role: otpApiRole,
              });
          if (!result.success) {
            lastSubmittedOtpRef.current = "";
            setDigits(buildEmptyDigits(MOBILE_OTP_LENGTH));
            setActiveIndex(0);
            focusFirstOtpBox();
            showOtpError(result.message || "Invalid verification code.");
            return;
          }
          onSuccess?.(result.message || (isEditMobileMode ? "Mobile number updated successfully." : "Mobile number verified successfully."));
          onClose();
          return;
        }

        const result = await validateEmailOtp({
          email,
          otpCode: code,
          purpose: OTP_PURPOSE_EMAIL_VERIFICATION,
          role: otpApiRole,
        });

        if (!result.success) {
          lastSubmittedOtpRef.current = "";
          setDigits(buildEmptyDigits(EMAIL_OTP_LENGTH));
          setActiveIndex(0);
          focusFirstOtpBox();
          showOtpError(result.message);
          return;
        }

        clearPersistedEmailVerifyResendLock();
        onSuccess?.(result.message || EMAIL_VERIFY_SUCCESS_MESSAGE);
        onClose();
      } catch (error) {
        Sentry.captureException(error);
        showOtpError("Unable to verify code. Please try again.");
      } finally {
        setIsVerifying(false);
      }
    },
    [clearErrorTimer, email, focusFirstOtpBox, isEditMobileMode, isEmailTarget, isVerifying, mobileNumber, onClose, onSuccess, otpApiRole, otpLength, profileApiRole, showOtpError, target, verificationRole],
  );

  useEffect(() => {
    if (!canSubmitOtp || otp.length !== otpLength || isVerifying) {
      return;
    }
    if (lastSubmittedOtpRef.current === otp) {
      return;
    }
    lastSubmittedOtpRef.current = otp;
    verifyOtp(otp);
  }, [otp, otpLength, canSubmitOtp, isVerifying, verifyOtp]);

  const handleResend = async () => {
    if (!canResend) return;
    // Keep the caret in the first box for the whole resend. The tap must not
    // leave focus on the button (see onMouseDown below) or iOS treats it as
    // "user left the field" and closes the keyboard, which nothing after the
    // await can reopen.
    focusFirstOtpBox();
    clearErrorTimer();
    setError("");
    setSendError("");
    setIsSending(true);
    try {
      const result = isEmailTarget
        ? await sendEmailOtp({
            email,
            purpose: OTP_PURPOSE_EMAIL_VERIFICATION,
            action: OTP_ACTION_RESEND,
            role: otpApiRole,
          })
        : isEditMobileMode
          ? await sendEditMobileOtp({
              emailId: email,
              role: profileApiRole,
              mobileNumber,
            })
          : await sendMobileOtp({
              mobileNumber,
              role: otpApiRole,
            });

      if (!result.success) {
        if (result.data) {
          if (isEmailTarget) {
            applyOtpMeta(result.data, email, result.message);
          } else {
            setOtpMeta(result.data);
            const cooldownEndsAt = resolveResendCooldownEndsAt(result.data.nextRequestAfter, result.message, result.data.minRequestIntervalSeconds);
            if (cooldownEndsAt) {
              setResendCooldownEndsAt(cooldownEndsAt);
              setResendCooldownSeconds(getCooldownRemainingSeconds(cooldownEndsAt));
            }
          }
        }

        if (isSendOtpRateLimitError(result.data)) {
          if (isEmailTarget) {
            const lockEndsAt = resolveEmailVerifyLockEndsAt({
              email,
              lockedUntil: result.data?.lockedUntil,
              attemptsRemaining: result.data?.attemptsRemaining ?? 0,
            });
            startResendLock(email, result.data);
            if (lockEndsAt) {
              onSendRateLimited?.(lockEndsAt);
            }
            onClose();
          } else {
            const lockEndsAt = parseLockedUntilMs(result.data?.lockedUntil) || (result.data?.attemptsRemaining === 0 ? Date.now() + RESEND_LOCK_MS : null);
            if (lockEndsAt) {
              onSendRateLimited?.(lockEndsAt);
              onClose();
            }
          }
          setSendError("");
          return;
        }

        if (isSendOtpTooSoonError(result.data, result.message) || isOtpWaitMessage(result.message)) {
          if (isEmailTarget && !result.data) {
            startResendCooldown(null, email, result.message);
          }
          setError("");
          return;
        }

        showOtpError(result.message);
        return;
      }

      if (isEmailTarget) {
        applyOtpMeta(result.data, email);
      } else if (result.data) {
        setOtpMeta(result.data);
        const cooldownEndsAt = resolveResendCooldownEndsAt(result.data.nextRequestAfter, "", result.data.minRequestIntervalSeconds);
        if (cooldownEndsAt) {
          setResendCooldownEndsAt(cooldownEndsAt);
          setResendCooldownSeconds(getCooldownRemainingSeconds(cooldownEndsAt));
        }
      }

      lastSubmittedOtpRef.current = "";
      setDigits(buildEmptyDigits(otpLength));
      setActiveIndex(0);
      setResent(true);
      focusFirstOtpBox();
      if (isMobileTarget) {
        requestSmsAutofill();
      }
    } catch (error) {
      Sentry.captureException(error);
      showOtpError("Failed to resend verification code.");
    } finally {
      setIsSending(false);
    }
  };

  const resendPrompt = target === "mobile" ? "Didn't receive the SMS?" : "Didn't receive the email?";

  return (
    <Dialog
      className="family-otp-verify-dialog"
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onClose(event, reason);
      }}
      maxWidth="xs"
      fullWidth
      disableAutoFocus
      disableRestoreFocus
      TransitionProps={{ onEntered: focusFirstOtpBox }}
      PaperProps={{ className: "family-otp-verify-paper" }}>
      <DialogContent className="family-modal-content family-otp-verify-content">
        <button type="button" className="family-modal-close" onClick={onClose} aria-label="Close">
          <IoClose />
        </button>

        {isSendingInitialCode && !isResendLockActive ? (
          <p className="family-otp-verify-intro" role="status">
            Sending verification code to <strong className="family-otp-verify-contact-strong">{contactDisplay}</strong>…
          </p>
        ) : otpReady ? (
          <p className="family-otp-verify-intro">
            A verification code has been sent to <strong className="family-otp-verify-contact-strong">{contactDisplay}</strong>. Enter the code below.
          </p>
        ) : null}

        {isResendLockActive && !otpReady ? (
          <p className="family-otp-verify-error" role="alert">
            {formatResendLockError(displayedLockSeconds)}
          </p>
        ) : sendError && !otpReady ? (
          <p className="family-otp-verify-error" role="alert">
            {sendError}
          </p>
        ) : null}

        {otpReady ? (
          <div className="family-otp-verify-form">
            <div className={`family-otp-boxes${otpLength > 4 ? " family-otp-boxes--compact" : ""}`} onPaste={handlePaste}>
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                  autoFocus={index === 0}
                  maxLength={1}
                  className={`family-otp-box${activeIndex === index ? " is-active" : ""}${error ? " has-error" : ""}`}
                  value={digit}
                  aria-label={`Digit ${index + 1} of ${otpLength}`}
                  onFocus={() => setActiveIndex(index)}
                  onChange={(e) => updateDigit(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onBeforeInput={handleBeforeInput}
                />
              ))}
            </div>

            {error ? (
              <p className="family-otp-verify-error" role="alert">
                {error}
              </p>
            ) : resent ? (
              <p className="family-otp-verify-resent" role="status">
                A new code has been sent. Please check your {isEmailTarget ? "email" : "messages"} and enter the new code above.
              </p>
            ) : null}

            {isVerifying ? (
              <p className="family-otp-verify-resent" role="status">
                Verifying…
              </p>
            ) : null}
          </div>
        ) : null}

        {otpReady && isEmailTarget && !isResendLockActive ? (
          <p className="family-otp-resend-line">
            {resendPrompt}{" "}
            <button type="button" className="family-otp-resend-link" onMouseDown={(event) => event.preventDefault()} onClick={handleResend} disabled={!canResend || isSending} aria-live="polite">
              {getResendButtonLabel()}
            </button>
          </p>
        ) : otpReady && !isEmailTarget ? (
          <p className="family-otp-resend-line">
            {resendPrompt}{" "}
            <button type="button" className="family-otp-resend-link" onMouseDown={(event) => event.preventDefault()} onClick={handleResend} disabled={!canResend || isSending} aria-live="polite">
              {getResendButtonLabel()}
            </button>
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default FamilyOtpVerifyDialog;
